<template>
  <!-- 用户消息（右） -->
  <div v-if="chatMessageItem.sender === 0 || chatMessageItem.sender === undefined" class="user msg">
    <div class="av av-user">
      <img :src="userPic" alt="用户" />
    </div>
    <div class="col">
      <div class="meta">{{ timeText }} · 我</div>
      <div class="bubble bubble-user">
        <p v-if="showMsg" class="msg-text">{{ showMsg }}</p>
        <div v-if="chatMessageItem.images && chatMessageItem.images.length" class="msg-imgs">
          <a-image v-for="(img, i) in chatMessageItem.images" :key="i" :src="img" :width="110" class="msg-img" />
        </div>
      </div>
    </div>
  </div>

  <!-- 机器人消息（左） -->
  <div v-if="chatMessageItem.sender === 1" class="msg bot">
    <div class="av av-bot">
      <icon-vue icon="lucide:bot-message-square" />
    </div>
    <div class="col">
      <div class="meta">
        <span>CTC-BOT · {{ timeText }}</span>
        <span class="copy" @click="copyContent">复制</span>
      </div>
      <!-- 正文走 markdown-it + DOMPurify；报告卡片(结构化)由 chat-content 从工具结果构建后挂在 reportCard 上 -->
      <div class="bubble bubble-bot markdown-body" v-html="renderedHtml"></div>
      <report-card v-if="chatMessageItem.reportCard" :data="chatMessageItem.reportCard" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/modules/user';
import { Icon as IconVue } from '@iconify/vue';
import { message } from 'ant-design-vue';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import { computed } from 'vue';
import ReportCard from './report-card.vue';
import type { ChatMessageItem } from './types';

const props = defineProps<{
  chatMessageItem: ChatMessageItem;
}>();

const user = useUserStore();

const md = new MarkdownIt({ html: false, linkify: true, breaks: true });

const timeText = computed(() =>
  props.chatMessageItem.created_at ? new Date(props.chatMessageItem.created_at).toLocaleString() : '',
);

// 复制整个文本
const copyContent = () => {
  navigator.clipboard
    .writeText(showMsg.value)
    .then(() => {
      message.success('复制成功');
    })
    .catch(() => {
      console.log('复制失败');
    });
};

const showMsg = computed(() => {
  if (props.chatMessageItem.content !== null) {
    if (props.chatMessageItem.content === '' && props.chatMessageItem.sender === 1) {
      return '思考中...';
    } else {
      return props.chatMessageItem.content;
    }
  } else {
    return '';
  }
});

// 机器人正文：markdown → HTML → 消毒
const renderedHtml = computed(() => DOMPurify.sanitize(md.render(showMsg.value || '')));

const userPic = computed(() => {
  return user.current?.avatar || 'https://res-cn.makeblock.com/passport/official/avatar/1.png';
});
</script>

<style scoped>
.msg {
  display: flex;
  gap: 8px;
  max-width: 100%;
}
.msg.user {
  flex-direction: row-reverse;
}
.av {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex: none;
  display: grid;
  place-items: center;
  overflow: hidden;
}
.av-bot {
  background: #e8f1ff;
  color: #1677ff;
}
.av-bot :deep(svg) {
  width: 17px;
  height: 17px;
}
.av-user img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  max-width: calc(100% - 40px);
}
.msg.user .col {
  align-items: flex-end;
}
.meta {
  font-size: 10.5px;
  color: #9aa3af;
  display: flex;
  gap: 8px;
  align-items: center;
}
.copy {
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
}
.msg.bot:hover .copy {
  opacity: 1;
}
.copy:hover {
  color: #1677ff;
}
.bubble {
  padding: 9px 12px;
  font-size: 13.5px;
  line-height: 1.6;
  border-radius: 12px;
  word-break: break-word;
  overflow-wrap: break-word;
}
.bubble-user {
  background: #1677ff;
  color: #fff;
  border-top-right-radius: 4px;
}
.bubble-bot {
  background: #eef2f7;
  color: #263041;
  border-top-left-radius: 4px;
}
.msg-text {
  margin: 0;
  white-space: pre-wrap;
}
.msg-imgs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.msg-imgs :deep(.ant-image img) {
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  object-fit: cover;
}

/* markdown 正文（v-html，需 :deep 命中内部元素）——紧凑聊天排版 */
.markdown-body :deep(> *:first-child) {
  margin-top: 0;
}
.markdown-body :deep(> *:last-child) {
  margin-bottom: 0;
}
.markdown-body :deep(p) {
  margin: 6px 0;
}
.markdown-body :deep(h1),
.markdown-body :deep(h2),
.markdown-body :deep(h3),
.markdown-body :deep(h4),
.markdown-body :deep(h5) {
  margin: 10px 0 6px;
  font-weight: 700;
  line-height: 1.3;
}
.markdown-body :deep(h1) {
  font-size: 16px;
}
.markdown-body :deep(h2) {
  font-size: 15px;
}
.markdown-body :deep(h3) {
  font-size: 14.5px;
}
.markdown-body :deep(h4),
.markdown-body :deep(h5) {
  font-size: 14px;
}
.markdown-body :deep(ul),
.markdown-body :deep(ol) {
  margin: 6px 0;
  padding-left: 20px;
}
.markdown-body :deep(li) {
  margin: 2px 0;
}
.markdown-body :deep(a) {
  color: #1677ff;
  text-decoration: none;
}
.markdown-body :deep(a:hover) {
  text-decoration: underline;
}
.markdown-body :deep(strong) {
  font-weight: 700;
}
.markdown-body :deep(code) {
  background: rgba(22, 119, 255, 0.1);
  color: #1668dc;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 12.5px;
}
.markdown-body :deep(pre) {
  background: #f6f8fa;
  padding: 8px 10px;
  border-radius: 8px;
  overflow-x: auto;
  margin: 6px 0;
}
.markdown-body :deep(pre code) {
  background: none;
  color: #263041;
  padding: 0;
}
.markdown-body :deep(blockquote) {
  margin: 6px 0;
  padding-left: 10px;
  border-left: 3px solid #d0d7de;
  color: #6b7280;
}
.markdown-body :deep(table) {
  border-collapse: collapse;
  font-size: 12.5px;
  display: block;
  overflow-x: auto;
}
.markdown-body :deep(th),
.markdown-body :deep(td) {
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
}
</style>
